import React from 'react';
import { ModalWrapper } from './ModalWrapper';
import { MODAL_WIDTHS, POPUP_STYLES } from '../../constants/popupStyles';

interface PaymentLinksModalProps {
    isOpen: boolean;
    onClose: () => void;
    patientName?: string;
    intakeLink?: string;
    followupLink?: string;
    onStatusMessage?: (message: { type: 'success' | 'error' | 'loading'; text: string } | null) => void;
}

/**
 * The Intake / Follow-up payment links for one patient, opened from the patient row
 * context menu. Both links come off the row itself (the backend sends them only for
 * clinics that charge the patient), so the popup makes no request of its own.
 */
export const PaymentLinksModal: React.FC<PaymentLinksModalProps> = ({
    isOpen,
    onClose,
    patientName,
    intakeLink,
    followupLink,
    onStatusMessage,
}) => {
    if (!isOpen) return null;

    const handleCopy = async (link: string, label: string) => {
        try {
            await navigator.clipboard.writeText(link);
            onStatusMessage?.({ type: 'success', text: `${label} payment link copied to clipboard` });
        } catch (error) {
            console.error('Failed to copy payment link:', error);
            onStatusMessage?.({ type: 'error', text: 'Failed to copy payment link' });
        }
    };

    const rows = [
        { label: 'Intake', link: intakeLink },
        { label: 'Follow-up', link: followupLink },
    ].filter(row => !!row.link) as Array<{ label: string; link: string }>;

    return (
        <ModalWrapper modalId="PaymentLinksModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Payment Links"
            width={MODAL_WIDTHS.xxl}
        >
            <div className="px-6 pb-2">
                {patientName && (
                    <div className="mb-4 text-[#366A75] font-semibold">{patientName}</div>
                )}

                {rows.length === 0 ? (
                    <div className="py-6 text-center text-[#366A75]">No payment links available</div>
                ) : (
                    <div className="space-y-3">
                        {rows.map(({ label, link }) => (
                            <div key={label} className="flex items-center gap-3">
                                <span className="font-semibold text-[#366A75] min-w-[90px]">{label}</span>
                                <a
                                    data-ai-id="payment-link"
                                    href={link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 px-4 py-2 rounded-lg bg-[#EBF5F5] border border-[#B4D9D4] text-[#3A7380] truncate"
                                    style={{ textDecoration: 'underline' }}
                                    title={link}
                                >
                                    {link}
                                </a>
                                <button
                                    data-ai-id="copy-payment-link"
                                    onClick={() => handleCopy(link, label)}
                                    className="p-2 text-[#366A75] hover:text-[#2D5A60] transition-colors"
                                    title={`Copy ${label} payment link`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="px-6 py-3 border-t border-[#B4D9D4] mt-4">
                <div className="flex items-center justify-center">
                    <button onClick={onClose} className={POPUP_STYLES.button}>Close</button>
                </div>
            </div>
        </ModalWrapper>
    );
};
